import { Bell, CreditCard, Megaphone, PackageOpen, Shield, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type React from 'react';
import { useEmailPreview } from '../hooks/useNotificationCampaigns';
import type {
  CampaignChannel,
  LocalizedContent,
  LocalizedLabel,
  NotificationCategory,
  NotificationLocale,
  NotificationSeverity,
} from '../types';

interface NotificationPreviewProps {
  category: NotificationCategory;
  severity: NotificationSeverity;
  content: LocalizedContent;
  ctaLabel: LocalizedLabel | null;
  ctaUrl: string | null;
  defaultLocale: NotificationLocale;
  locale: NotificationLocale;
  channels?: CampaignChannel[];
  emailPreviewPublicId?: string;
  emailPreviewDirty?: boolean;
  onLocaleChange: (locale: NotificationLocale) => void;
}

const categoryIcons: Record<NotificationCategory, React.ReactNode> = {
  SYSTEM: <Sparkles size={18} />,
  SECURITY: <Shield size={18} />,
  BILLING: <CreditCard size={18} />,
  PRODUCT_UPDATE: <PackageOpen size={18} />,
  MARKETING: <Megaphone size={18} />,
};

const stripMarkdown = (value: string) =>
  value
    .replace(/[`*_>#-]/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

const useDebouncedValue = <T,>(value: T, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

export const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  category,
  severity,
  content,
  ctaLabel,
  ctaUrl,
  defaultLocale,
  locale,
  channels = ['IN_APP'],
  emailPreviewPublicId,
  emailPreviewDirty = false,
  onLocaleChange,
}) => {
  const [tab, setTab] = useState<'in_app' | 'email'>('in_app');
  const localized = content[locale] || content[defaultLocale] || { title: '', body: '' };
  const cta = ctaLabel?.[locale] || ctaLabel?.[defaultLocale] || '';
  const bodyPreview = stripMarkdown(localized.body || 'Notification body preview').slice(0, 160);
  const emailEnabled = channels.includes('EMAIL');
  const debouncedLocale = useDebouncedValue(locale, 400);
  const emailPreview = useEmailPreview(
    emailPreviewPublicId,
    debouncedLocale,
    tab === 'email' && emailEnabled && Boolean(emailPreviewPublicId) && !emailPreviewDirty
  );

  useEffect(() => {
    if (!emailEnabled) setTab('in_app');
  }, [emailEnabled]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
          <Bell size={18} />
          Preview
        </div>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={locale}
          onChange={(_, next) => next && onLocaleChange(next)}
        >
          <ToggleButton value="vi">vi</ToggleButton>
          <ToggleButton value="en">en</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {emailEnabled && (
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
          <Tab value="in_app" label="In-app" />
          <Tab value="email" label="Email" />
        </Tabs>
      )}

      {tab === 'in_app' && (
        <>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-3 flex items-start gap-3">
              <div className="rounded-full bg-blue-50 p-2 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                {categoryIcons[category]}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">
                  <span>{category.replace('_', ' ')}</span>
                  <span>{severity}</span>
                </div>
                <h3 className="mt-1 line-clamp-1 font-semibold text-gray-900 dark:text-white">
                  {localized.title || 'Notification title'}
                </h3>
                <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-slate-300">
                  {bodyPreview}
                </p>
              </div>
            </div>
            {cta && ctaUrl && (
              <Button size="small" variant="outlined">
                {cta}
              </Button>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {localized.title || 'Notification title'}
            </h3>
            <div className="mt-3 whitespace-pre-wrap text-sm text-gray-700 dark:text-slate-200">
              {stripMarkdown(localized.body || 'Notification body')}
            </div>
            {cta && ctaUrl && (
              <Button className="mt-4" variant="contained">
                {cta}
              </Button>
            )}
          </div>
        </>
      )}

      {tab === 'email' && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          {emailPreviewDirty ? (
            <Alert severity="info">Save draft to refresh email preview.</Alert>
          ) : emailPreview.isFetching ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
              <CircularProgress size={18} /> Loading email preview...
            </div>
          ) : emailPreview.isError ? (
            <Alert severity="warning">Unable to load email preview.</Alert>
          ) : emailPreview.data ? (
            <div className="space-y-3">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {emailPreview.data.subject}
              </div>
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={emailPreview.data.html}
                className="h-[560px] w-full rounded border border-gray-200 bg-white dark:border-slate-700"
              />
            </div>
          ) : (
            <Alert severity="info">Save draft to refresh email preview.</Alert>
          )}
        </div>
      )}
    </div>
  );
};
